/**
 * CrmDealSourceFilter - Filtro de Origem de Negócios (multi-seleção)
 *
 * Permite selecionar UMA ou MAIS origens (source) das opções existentes nos deals
 * do tenant. Usa useDealFilterOptions para popular as opções dinamicamente.
 *
 * @version 2.0.0
 * RELEASE: 2026-10-05 v2.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 * RELEASE: 2026-07-11 v2.0.0 - Convertido para multi-seleção (compatível com
 *          filtros p_source_ids das RPCs get_deals_paginated / get_kanban_column).
 * RELEASE: 2026-04-23 v1.0.0 - Implementação inicial (single).
 */

import React, { useState } from 'react';
import { Check, ChevronDown, Megaphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useDealFilterOptions } from '@/hooks/useDealFilterOptions';
import { getLeadSourceIcon, getLeadSourceLabel } from '@/lib/lead-sources';

/**
 * Props do componente
 * @param value - Origens selecionadas (array vazio = todas)
 * @param onChange - Callback de mudança
 * @param className - Classes CSS adicionais
 */
interface CrmDealSourceFilterProps {
  value: string[];
  onChange: (sources: string[]) => void;
  className?: string;
}

/**
 * Filtro de origem (source) — multi-seleção
 */
export function CrmDealSourceFilter({ value, onChange, className }: CrmDealSourceFilterProps) {
  const [open, setOpen] = useState(false);
  const { sources } = useDealFilterOptions();

  // Normaliza selectedSet para lookups O(1)
  const selectedSet = new Set(value ?? []);

  // Toggle de uma origem (adiciona/remove do array)
  const toggleSource = (source: string) => {
    if (selectedSet.has(source)) {
      onChange(value.filter((v) => v !== source));
    } else {
      onChange([...value, source]);
    }
  };

  // Limpa todas as origens selecionadas
  const clearAll = () => {
    onChange([]);
  };

  // Label exibido no botão
  const buttonLabel = (() => {
    if (value.length === 0) return 'Origem';
    if (value.length === 1) return getLeadSourceLabel(value[0]);
    return `${value.length} origens`;
  })();

  // Ícone da 1a origem quando exatamente 1 selecionada
  const SelectedIcon = value.length === 1 ? getLeadSourceIcon(value[0]) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'gap-1.5 font-normal border-0 bg-background rounded-full',
            value.length > 0 && 'border-primary bg-primary/5',
            className,
          )}
        >
          {SelectedIcon ? (
            <SelectedIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <Megaphone className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate">{buttonLabel}</span>
          {value.length > 1 && (
            <Badge variant="secondary" className="h-4 px-1 text-[10px] bg-primary/15 text-primary border-0">
              {value.length}
            </Badge>
          )}
          <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar origem..." />
          <CommandList>
            <CommandEmpty>Nenhuma origem encontrada.</CommandEmpty>
            {value.length > 0 && (
              <>
                <CommandGroup>
                  <CommandItem
                    value="__clear__"
                    onSelect={clearAll}
                    className="cursor-pointer text-muted-foreground"
                  >
                    <div className="mr-2 h-4 w-4" />
                    <span>Limpar seleção</span>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            {sources.length > 0 && (
              <CommandGroup heading="Origens">
                {sources.map((source) => {
                  const SourceIcon = getLeadSourceIcon(source);
                  const sourceLabel = getLeadSourceLabel(source);
                  const isSelected = selectedSet.has(source);
                  return (
                    <CommandItem
                      key={source}
                      value={source}
                      onSelect={() => toggleSource(source)}
                      className="cursor-pointer"
                    >
                      <div
                        className={cn(
                          'mr-2 h-4 w-4 rounded border flex items-center justify-center shrink-0',
                          isSelected ? 'border-primary bg-primary' : 'border-muted',
                        )}
                      >
                        {isSelected && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                      </div>
                      {SourceIcon && (
                        <SourceIcon className="h-3.5 w-3.5 mr-2 text-muted-foreground shrink-0" />
                      )}
                      <span className="truncate">{sourceLabel}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
